import React, { useCallback, useEffect, useRef, useState } from "react";
import { useBetSlip } from "../providers/BetSlipProvider";
import { useActiveKeyboard } from "../providers/ActiveKeyboardProvider";
import StakeInput from "./StakeInput";
import VirtualKeyboard from "./VirtualKeyboard";
import FreeBets from "./FreeBets";
import { useBonuses } from "../providers/BonusProvider";
import useModal from "../hooks/useModal";
import SuccessPlaceBetModal from "./SuccessPlaceBetModal";
import ErrorMessage from "./ErrorMessage";
import { usePlaceBet } from "../hooks/usePlaceBet";
import { useWidgetContext } from "../providers/WidgetProvider";
import { useI18n } from "../providers/I18nProvider";
import { formatNumber } from "../shared/utils/helpers";
import { BetSlipMode, Layout, PlaceBetStatus, StateActionTypes } from "../types/enums";
import { useConfirmBetChangesData } from "../hooks/useConfirmBetChangesData";
import {
  DEFAULT_CULTURE_LOCALE,
  TOTAL_STAKE_KEYBOARD_ID,
  twoLetterCultureMap,
} from "../shared/constants";
import { createPlaceBetRequestBody } from "../shared/utils/bet-slip";
import { getModalTitleKey } from "../shared/utils/bet-slip-place-validation";
import { PlaceBetErrorModal } from "./PlaceBetErrorModal";
import { useBetSlipValidation } from "../hooks/useBetSlipValidation";
import { useSummaryCalculations } from "../hooks/useSummaryCalculations";
import { useSummaryModals } from "../hooks/useSummaryModals";
import { SummaryRows } from "./SummaryRows";
import { SummaryActions } from "./SummaryActions";

export const Summary: React.FC = () => {
  const [bookingCode, setBookingCode] = useState("263233");
  const [bookingValidTime, setBookingValidTime] = useState(48);
  const [betResult, setBetResult] = useState<ExecuteOrderResponse | null>(null);
  const [keyboardValue, setKeyboardValue] = useState("");
  const { activeKeyboardId, openKeyboard, closeKeyboard } = useActiveKeyboard();
  const isKeyboardVisible = activeKeyboardId === TOTAL_STAKE_KEYBOARD_ID;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [useFreeBet, setUseFreeBet] = useState(false);
  const [placeBetErrorKey, setPlaceBetErrorKey] =
    useState<PlaceBetModalErrorKey | null>(null);

  // Snapshot of stakes taken just before the confirm-changes modal opens.
  // Ref is used to restore on cancel; state is used for modal "original" display.
  const preChangesStakesRef = React.useRef<{
    globalStake: number;
    individualStakes: { [key: string]: number };
  } | null>(null);

  const [preChangesStakes, setPreChangesStakes] = useState<{
    globalStake: number;
    individualStakes: { [key: string]: number };
  } | null>(null);

  // Snapshot of the live total odds taken just before the confirm-changes
  // modal opens, mirroring preChangesStakes above - the modal's "Original"
  // odds must reflect the price before UPDATE_SELECTIONS_FROM_RECOVERY
  // overwrites the selections with the new price below.
  const [preChangesOdds, setPreChangesOdds] = useState<number | null>(null);

  const {
    betSlipState,
    config,
    stakes: individualStakes,
    globalStake,
    updateGlobalStake,
    updateStake,
    selectedBonus,
    selectBonus,
    clearAllSelections,
    onOrderExecuted,
  } = useBetSlip();

  const { t, tHtml } = useI18n();
  const { dispatch } = useWidgetContext();
  const { rawBonuses, refetchBonuses } = useBonuses();

  const calcs = useSummaryCalculations({
    betSlipState,
    config,
    individualStakes,
    globalStake,
    selectedBonus,
    rawBonuses,
  });

  const currentStake = calcs.isFreeBetActive ? calcs.freeBetStake : globalStake;

  const useVirtualKeyboard =
    config.kiosk.isKiosk || config.kiosk.layout === Layout.Mobile;

  useEffect(() => {
    setKeyboardValue(currentStake > 0 ? currentStake.toString() : "");
  }, [currentStake]);

  const { validate } = useBetSlipValidation();
  const [SuccessModal, toggleSuccessModal] = useModal(SuccessPlaceBetModal);
  const [PlaceBetErrorModalComponent, togglePlaceBetErrorModal] =
    useModal(PlaceBetErrorModal);

  // Ref breaks the circular dep: usePlaceBet callback → modals (declared below)
  const toggleBookBetModalRef = useRef<(() => void) | null>(null);

  const {
    executeBet,
    abortBet,
    declineBetChanges,
    confirmBetChanges,
    clearAuthState,
    authState,
    isLoading,
    isBookingLoading,
  } = usePlaceBet({
    onPlaceBetSuccess: (response) => {
      setErrorMessage(null);
      setBetResult(response);
      toggleSuccessModal();
      onOrderExecuted?.(response);
      refetchBonuses();
    },
    onBookBetSuccess: (code, response) => {
      setBookingCode(code);
      setBookingValidTime(response.validTime);
      toggleBookBetModalRef.current?.();
    },
    onError: (error) => {
      if (error !== "Bet was declined") {
        const translated = t(error as keyof TranslationKeys);
        setErrorMessage(translated ?? error);
      }
    },
    onRecoverBetSlip: (additionalInfo, scheduleRetry) => {
      dispatch({
        type: StateActionTypes.UPDATE_SELECTIONS_FROM_RECOVERY,
        payload: additionalInfo,
      });
      if (betSlipState.acceptOddsChanges && scheduleRetry) scheduleRetry();
    },
    onBonusRulesChanged: () => {
      setErrorMessage("BonusRulesChanged");
      setTimeout(() => window.location.reload(), 2000);
    },
  });

  const modals = useSummaryModals(authState);

  useEffect(() => {
    toggleBookBetModalRef.current = modals.toggleBookBetModal;
  }, [modals.toggleBookBetModal]);

  const confirmBetChangesData = useConfirmBetChangesData({
    message: authState.message as AuthMessageLike | null,
    betSlipState,
    individualStakes,
    globalStake,
    taxConfig: calcs.taxConfig,
    selectedBonus: selectedBonus || undefined,
    winBoostAmount: calcs.winBoostAmount,
    preChangesIndividualStakes: preChangesStakes?.individualStakes,
    preChangesGlobalStake: preChangesStakes?.globalStake,
    preChangesOdds,
  });

  // When the confirm-changes modal opens: snapshot current stakes for display,
  // apply rowChanges to the background betslip, and apply betChanges odds to Redux.
  // When the modal closes, clear the snapshot.
  useEffect(() => {
    if (authState.status !== PlaceBetStatus.ConfirmChanges) {
      setPreChangesStakes(null);
      setPreChangesOdds(null);
      return;
    }

    const snapshot = { globalStake, individualStakes };
    preChangesStakesRef.current = snapshot;
    setPreChangesStakes(snapshot);
    setPreChangesOdds(calcs.totalOdds);

    if (authState.message?.rowChanges?.length) {
      if (betSlipState.mode === BetSlipMode.Combo) {
        updateGlobalStake(authState.message.rowChanges[0].newAmount);
      } else {
        authState.message.rowChanges.forEach((rc) => {
          const selection = betSlipState.selections.find((s) =>
            rc.selections.includes(s.eventId),
          );
          if (selection) updateStake(selection.key, rc.newAmount);
        });
      }
    }

    const changedBets = authState.message?.betChanges?.filter((bc) => bc.isChanged) ?? [];
    if (changedBets.length) {
      const recoveryChanges = changedBets.flatMap((bc) =>
        bc.selections.map((eventId) => ({
          ev_id: eventId,
          bt_id: bc.betTypeId,
          o_p: bc.newPrice,
        })),
      );
      dispatch({ type: StateActionTypes.UPDATE_SELECTIONS_FROM_RECOVERY, payload: recoveryChanges });
    }
  }, [authState.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlaceBet = useCallback(
    async ({ isBooking }: { isBooking: boolean }) => {
      closeKeyboard();
      const { inlineError, modalErrorKey } = validate({
        mode: betSlipState.mode,
        totalStake: calcs.totalStake,
        individualStakeValues: Object.values(individualStakes),
        totalSelections: calcs.totalSelections,
        isBooking,
        isFreeBet: calcs.isFreeBetActive,
      });
      if (inlineError) {
        setErrorMessage(inlineError);
        return;
      }
      setErrorMessage(null);
      if (modalErrorKey) {
        setPlaceBetErrorKey(modalErrorKey);
        togglePlaceBetErrorModal();
        return;
      }
      try {
        const cultureParts = config.culture.replace(/\//g, "");
        const language =
          twoLetterCultureMap[cultureParts] ||
          cultureParts ||
          DEFAULT_CULTURE_LOCALE;

        const bonusIds: BonusId[] = [];

        if (selectedBonus && !isBooking && selectedBonus.userBonus) {
          bonusIds.push({
            typeId: selectedBonus.bonusTypeId,
            id: selectedBonus.userBonus[0].id!,
          });
        }

        if (!isBooking && calcs.activeWinBoost && !selectedBonus) {
          bonusIds.push({
            typeId: calcs.activeWinBoost.originalBonus.bonusTypeId,
            id: calcs.activeWinBoost.originalBonus.id,
            minStakeAmount:
              calcs.activeWinBoost.originalBonus.minStakeAmount || 1,
          });
        }

        const requestBody = createPlaceBetRequestBody({
          brandId: config.brand.id,
          channelId: config.channelId,
          betSlipState,
          language,
          userToken: config.user.token || null,
          bookingCode: "",
          totalStake: calcs.totalStake,
          bonusIds,
          isBooking,
          stakes: calcs.stakes,
        });

        await executeBet(requestBody);
      } catch {
        setErrorMessage("PleaseTryAgainLater");
      }
    },
    [
      config,
      betSlipState,
      calcs.totalStake,
      calcs.totalSelections,
      calcs.isFreeBetActive,
      calcs.activeWinBoost,
      calcs.stakes,
      selectedBonus,
      executeBet,
      individualStakes,
      closeKeyboard,
    ],
  );

  const handleDeclineBet = useCallback(() => {
    preChangesStakesRef.current = null;
    clearAllSelections();
    declineBetChanges();
  }, [clearAllSelections, declineBetChanges]);

  const handleAbortBet = useCallback(() => {
    clearAllSelections();
    abortBet();
  }, [clearAllSelections, abortBet]);

  const handleConfirmBet = useCallback(async () => {
    preChangesStakesRef.current = null;
    await confirmBetChanges();
  }, [confirmBetChanges]);

  const handleContinueBetting = useCallback(() => {
    toggleSuccessModal();
    setBetResult(null);
    setUseFreeBet(false);
    selectBonus(null);
    if (clearAllSelections) clearAllSelections();
  }, [toggleSuccessModal, clearAllSelections, selectBonus]);

  const handleReuseSelections = useCallback(() => {
    toggleSuccessModal();
    setBetResult(null);
    setUseFreeBet(false);
    selectBonus(null);
    updateGlobalStake(0);
  }, [toggleSuccessModal, selectBonus, updateGlobalStake]);

  const handleDeclinedModalContinue = useCallback(() => {
    clearAuthState();
    modals.toggleDeclinedModal();
  }, [clearAuthState, modals]);

  const handleStakeChange = (value: string) => {
    updateGlobalStake(parseFloat(value) || 0);
  };

  const handleKeyboardValueChange = (value: string) => {
    setKeyboardValue(value);
    updateGlobalStake(parseFloat(value) || 0);
  };

  const handleDismissError = useCallback(() => setErrorMessage(null), []);

  const handleStakeInputFocus = () => {
    if (useVirtualKeyboard && !isKeyboardVisible) {
      openKeyboard(TOTAL_STAKE_KEYBOARD_ID);
      setKeyboardValue(currentStake > 0 ? currentStake.toString() : "");
    }
  };

  const placeBetErrorTitleKey = placeBetErrorKey
    ? getModalTitleKey(placeBetErrorKey)
    : null;
  const placeBetErrorTitle = placeBetErrorTitleKey
    ? t(placeBetErrorTitleKey)
    : "Bet Not Complete";

  return (
    <div className="bg-betslip-summary-bg border-t pt-2 space-y-4">
      <div className="border-t border-gray-300"></div>

      <div className="flex">
        <FreeBets useFreeBet={useFreeBet} setUseFreeBet={setUseFreeBet} />

        {!useFreeBet &&
          (betSlipState.selections.length > 1 ||
            betSlipState.betBuilderState.bets.length > 0) && (
            <div className="space-y-2 px-4 ml-auto">
              <div className="flex items-center justify-end">
                <StakeInput
                  value={currentStake > 0 ? currentStake : undefined}
                  onStakeChange={handleStakeChange}
                  currency={config.user.currency}
                  placeholder={t("BetSlipComboStake")}
                  className="w-16 bg-input-bg text-md focus:outline-none text-right"
                  onFocus={handleStakeInputFocus}
                  readOnly={useVirtualKeyboard}
                  disabled={!!errorMessage}
                />
              </div>
              <div className="text-sm text-gray-500 text-right"
                dangerouslySetInnerHTML={tHtml("BetSlipMinStakeInformation", { minBet: config.user.minBet })}
              />
            </div>
          )}
      </div>

      {useVirtualKeyboard && isKeyboardVisible && (
        <VirtualKeyboard
          value={keyboardValue}
          onValueChange={handleKeyboardValueChange}
          onClose={closeKeyboard}
          currency={config.user.currency}
          chips={(
            config.brand.currencyConfigurations?.[config.user.currencyId]
              ?.keyboard?.chips?.items ??
            config.kiosk.keyboard?.chips?.items
          )?.map((item) => ({
            action:
              item.action === "+" || item.action === "set"
                ? "add"
                : "clear",
            value: item.value,
            label: item.content.toString(),
          }))}
        />
      )}
      {calcs.isFreeBetActive && (
        <div className="space-y-2 px-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{t("BetSlipTotalStake", { "receiptStake:span": "" }).trim()}</span>
            <span className="text-sm font-semibold">
              {formatNumber(calcs.totalStake)} {config.user.currency}
            </span>
          </div>
        </div>
      )}

      <ErrorMessage
        message={errorMessage}
        onDismiss={handleDismissError}
        autoHideDuration={4000}
      />

      <SummaryRows
        mode={betSlipState.mode}
        showVat={calcs.showVat}
        exciseTaxRate={calcs.exciseTaxRate}
        exciseTaxValue={calcs.exciseTaxValue}
        netStake={calcs.netStake}
        individualStakes={individualStakes}
        totalOdds={calcs.totalOdds}
        activeWinBoost={calcs.activeWinBoost}
        isWinBoostEligible={calcs.isWinBoostEligible}
        winBoostAmount={calcs.winBoostAmount}
        isFreeBetActive={calcs.isFreeBetActive}
        taxPercentage={calcs.taxPercentage}
        totalTaxValue={calcs.totalTaxValue}
        maxPayout={calcs.maxPayout}
        currency={config.user.currency}
      />

      <SummaryActions
        hasOddsChanges={calcs.hasOddsChanges}
        hasClosedOrSuspendedSelections={calcs.hasClosedOrSuspendedSelections}
        isPlacingBet={isLoading && !isBookingLoading}
        isBookingBet={isBookingLoading}
        isAuthorizing={!!authState.key}
        timeoutSecondsLeft={
          authState.status === PlaceBetStatus.Timeout ? authState.secondsLeft : undefined
        }
        isLoggedIn={config.user.isAuthenticated}
        cultureUrl={config.culture}
        disableFreeBetPlacement={calcs.isFreeBetActive && !calcs.isFreeBetEligible}
        onPlaceBet={handlePlaceBet}
        onAcceptOddsChanges={() =>
          dispatch({ type: StateActionTypes.ACCEPT_ODDS_CHANGES })
        }
      />

      <modals.BookBetModal
        title={t("BetSlipBookSuccessTitle")}
        bookingCode={bookingCode}
        validTime={bookingValidTime}
        onClose={modals.toggleBookBetModal}
      />

      <modals.AuthModal
        title={t("BetSlipBetAuthorizationTitle")}
        secondsLeft={authState.secondsLeft}
        totalSeconds={authState.totalSeconds}
        onAbort={handleAbortBet}
        onClose={handleAbortBet}
      />

      <modals.DeclinedModal
        title={t("BetSlipBetAuthorizationTitle2")}
        onContinue={handleDeclinedModalContinue}
        onClose={handleDeclinedModalContinue}
      />

      {authState.message && (
        <modals.ConfirmModal
          title={t("BetSlipBetAuthorizationTitle3")}
          onConfirm={handleConfirmBet}
          onDecline={handleDeclineBet}
          onClose={handleDeclineBet}
          changes={confirmBetChangesData?.changes ?? []}
          originalStake={
            confirmBetChangesData?.originalStake ?? calcs.totalStake
          }
          newStake={confirmBetChangesData?.newStake ?? calcs.totalStake}
          originalOdds={confirmBetChangesData?.originalOdds ?? calcs.totalOdds}
          newOdds={confirmBetChangesData?.newOdds ?? calcs.totalOdds}
          oddsChanged={confirmBetChangesData?.oddsChanged}
          stakeChanged={confirmBetChangesData?.stakeChanged}
          exciseTaxValue={confirmBetChangesData?.newExciseTaxValue}
          exciseTaxRate={confirmBetChangesData?.newExciseTaxRate}
          eventName={confirmBetChangesData?.eventName ?? "Event"}
          winningTax={confirmBetChangesData?.winningTax ?? calcs.totalTaxValue}
          newWinningTax={
            confirmBetChangesData?.newWinningTax ?? calcs.totalTaxValue
          }
          maxPayout={confirmBetChangesData?.maxPayout ?? calcs.maxPayout}
          newMaxPayout={confirmBetChangesData?.newMaxPayout ?? calcs.maxPayout}
          taxPercentage={
            confirmBetChangesData?.taxPercentage ?? calcs.taxPercentage
          }
          currency={config.user.currency}
        />
      )}

      {placeBetErrorKey && (
        <PlaceBetErrorModalComponent
          title={placeBetErrorTitle}
          messageKey={placeBetErrorKey}
          cultureUrl={config.culture}
          onClose={togglePlaceBetErrorModal}
        />
      )}

      {betResult && (
        <SuccessModal
          title={t("BetSlipBetPlaced")}
          orderNumber={betResult.orderNumber}
          barcode={betResult.barcode}
          stake={betResult.netStake + (betResult.vatValue || 0)}
          totalOdds={betResult.totalOdds}
          maxPayout={betResult.maxPayout}
          totalBonus={betResult.totalBonus > 0 ? betResult.totalBonus : undefined}
          bonusPercent={calcs.activeWinBoost?.percentage}
          totalTax={betResult.totalTax > 0 ? -betResult.totalTax : betResult.totalTax}
          taxPercentage={calcs.taxPercentage}
          vatValue={betResult.vatValue}
          vatPercentage={betResult.vatPercentage}
          netStake={betResult.netStake}
          currency={config.user.currency}
          onContinue={handleContinueBetting}
          onReuseSelections={handleReuseSelections}
          onClose={handleReuseSelections}
        />
      )}
    </div>
  );
};

export default Summary;
